import {regNavn} from "felles/tjenester/tredjepartsTjenestenavn";
import {IFormController, INgModelController, IPromise, IQService} from "angular";
import {l} from "types/typescriptdefinisjoner_lederplan";
import {WebNavigasjon} from "web/tjenester/WebNavigasjon";
import {gettextCatalog} from "angular-gettext";
import {Dialog} from "../../tjenester/Dialog";
import _ from "lodash";
import {Kontekst} from "felles/tjenester/Kontekst";
import {Debugger} from "debug";
import {PlanRessurs} from "felles/tjenester/ressurser/PlanRessurs";
import moment from "moment";
import {RedigerMøtemalDialog} from "../gruppe/modale/redigerMøtemalDialog/redigerMøtemalDialog";
import {DashboardFiltervalg} from "../dashboard/komponenter/dashboardFilter/dashboardFilter";
import {UIRouterGlobals} from "@uirouter/angularjs";

const debug: Debugger = require('debug')('OpprettPlanKontroller');

require('./opprettPlan.scss');

/**
 * Kontroller for opprett plan.
 */
export class OpprettPlanKontroller {

    static regNavn = "OpprettPlanKontroller";

    static forInjisering = () => {
        return [
            regNavn.ng.$q,
            regNavn.ng.ui.$uiRouterGlobals,
            regNavn.md.$mdDialog,
            PlanRessurs.regNavn,
            Dialog.regNavn,
            regNavn.gettext.gettextCatalog,
            WebNavigasjon.regNavn,
            Kontekst.regNavn,
            OpprettPlanKontroller
        ]
    };

    constructor(private $q: IQService,
                private $uiRouterGlobals: UIRouterGlobals,
                private $mdDialog: angular.material.IDialogService,
                private planRessurs: PlanRessurs,
                private dialog: Dialog,
                private gettextCatalog: gettextCatalog,
                private webNavigasjon: WebNavigasjon,
                kontekst: Kontekst) {
        const forespurtGruppeId = this.$uiRouterGlobals.params['gruppeId'];
        const forespurtStart = this.$uiRouterGlobals.params['start'];
        const forespurtSlutt = this.$uiRouterGlobals.params['slutt'];

        this.modell = new OpprettPlanModell(kontekst.autentiserteGrupper());
        if (!_.isEmpty(forespurtGruppeId)) {
            this.modell.gruppe = kontekst.finnAutentiserteGruppe(forespurtGruppeId);
        }
        if (!_.isEmpty(forespurtStart) && !_.isEmpty(forespurtSlutt)) {
            this.modell.settPeriodeOgÅr((new Date(forespurtStart)), (new Date(forespurtSlutt)));
            if (!_.isNil(this.modell.gruppe))
                this.modell.trinn = OpprettPlanTrinn.vismoetemal
        }
    }

    modell: OpprettPlanModell;

    eksisterendePlaner:l.PlanModell[] = null;
    visEksisterendePlaner = false;
    overlappendePlaner: l.PlanModell[] = [];
    antDagerGap = 0;
    moetemal: l.MalmøteModell = null;

    progresjon(){
        return (this.modell.trinn +1) * (100 / ((_.size(OpprettPlanTrinn) / 2)+1));
    }

    gruppeEndret() {
        this.eksisterendePlaner = null;
        this.visEksisterendePlaner = false;
        this.modell.reset();
    }

    aarstallOppdatert(årstallFelt: INgModelController) {
        this.modell.setÅr(this.modell.aarstall);

        //gi et hint dersom det er et gap mellom plan start og slutten på foregående plan
        const foregåendePlan = this.eksisterendePlaner.slice().reverse().filter(plan => plan.periode.slutt.getTime() < this.modell.spesifikasjon.periode.start.getTime());
        if (foregåendePlan.length == 0) return; //ingen tidligere planer
        this.antDagerGap = moment(this.modell.periode.start).diff(foregåendePlan[0].periode.slutt, 'days');
    }

    //datoOppdatert er knyttet til gui der man kunne velge fra-til dato - ikke slettet da det er aktuelt å gjeninnføre denne muligheten
    datoOppdatert(startdatoFelt: INgModelController, sluttdatoFelt: INgModelController) {
        this.antDagerGap = 0;
        if (_.isNil(this.modell.periode.start) || _.isNil(this.modell.periode.slutt)) return;
        this.modell.settPeriode(this.modell.periode.start, this.modell.periode.slutt);//for at spesifikasjon skal oppdateres

        sluttdatoFelt.$setValidity("sluttFoerStart", this.modell.periode.start.getTime() < this.modell.spesifikasjon.periode.slutt.getTime());

        const varighet = moment(this.modell.periode.slutt).diff(this.modell.periode.start, 'days');
        sluttdatoFelt.$setValidity("periodeForKort", varighet > 30);

        this.overlappendePlaner = this.beregnOverlappendePlaner();
        startdatoFelt.$setValidity("overlapper", this.overlappendePlaner.length == 0);

        if (startdatoFelt.$invalid || sluttdatoFelt.$invalid) return;

        //gi et hint dersom det er et gap mellom plan start og slutten på foregående plan
        const foregåendePlan = this.eksisterendePlaner.slice().reverse().filter(plan => plan.periode.slutt.getTime() < this.modell.periode.start.getTime());
        if (foregåendePlan.length == 0) return; //ingen tidligere planer
        this.antDagerGap = moment(this.modell.periode.start).diff(foregåendePlan[0].periode.slutt, 'days');
    }

    visEksisterendePlanperioder(){
        this.visEksisterendePlaner = true;
    }

    skjulEksisterendePlanperioder(){
        this.visEksisterendePlaner = false;
    }

    redigerMoetemal() {
        RedigerMøtemalDialog.visRedigerMøtemalDialog(this.modell.gruppe, this.$mdDialog)
          .then(oppdatertMal => this.moetemal = oppdatertMal)
          .catch(_.noop);
    }

    nesteTrinn() {
        this.modell.trinn++;
        if (this.modell.trinn === OpprettPlanTrinn.velgperiode) this.initVelgPeriodeTrinn();
        if (this.modell.trinn === OpprettPlanTrinn.velgKopierPlanForKopiering) {
            this.initVelgKopierPlanForKopieringTrinn().then(eksisterendePlaner => {
                //hopp over "velg plan å kopiere fra" trinnet dersom ingen planer med møter eksisterer
                if (_.isEmpty(eksisterendePlaner.filter(plan => !_.isEmpty(plan.moeter))))
                    this.modell.trinn++;
            });
        }
    }

    forrigeTrinn() {
        if (this.modell.trinn === OpprettPlanTrinn.visplan && this.modell.antallMoeter() > 0) {
            this.dialog.confirm(
              this.gettextCatalog.getString('Bekreft navigasjon'),
              this.gettextCatalog.getString('Dersom du går tilbake vil møter du har lagt til her bli borte'),
              this.gettextCatalog.getString('Gå tilbake'),
              this.gettextCatalog.getString('Avbryt'),
            )
              .then(() => this.gåTilForrigeTrinn())
              .catch(()=>{}); //bruker avbrøt

        } else
            this.gåTilForrigeTrinn();
    }

    erSkjemaGyldig(skjema:IFormController) {
        switch (this.modell.trinn) {
            case OpprettPlanTrinn.velggruppe:
                return this.modell.gruppe != null;
            case OpprettPlanTrinn.velgperiode:
                return ((<INgModelController>skjema['aarstall']).$valid);
                //return ((<INgModelController>skjema['startdato']).$valid && (<INgModelController>skjema['sluttdato']).$valid);
            default:
                return true;
        }
    }

    vedMoetespesifikasjonerOppdatert(nyttMøteSpesifikasjoner: l.NyttMøteSpesifikasjon[], kopierMøteSpesifikasjoner: l.KopierMøteSpesifikasjon[]){
        this.modell.spesifikasjon.nyeMoeter = nyttMøteSpesifikasjoner;
        this.modell.spesifikasjon.kopierteMoeter = kopierMøteSpesifikasjoner;
    }

    opprettPlan() {
        this.dialog.confirm(
            ///dialogtittel for dialog ved opprettelse av møter i plan-wizard
            this.gettextCatalog.getString('Bekreft opprettelse av møter'),
            ///dialoginnhold for dialog ved opprettelse av møter i plan-wizard
            this.gettextCatalog.getString('Du er i ferd med å opprette {{antall}} møter i årsplanen {{planstart}} - {{planslutt}} i {{gruppetittel}} i {{organisasjonsnavn}}',{
                antall:this.modell.antallMoeter(),
                planstart: moment(this.modell.periode.start).utc(false).format('L'), //dato er opprinnelig i UTC - kaller derfor .utc(..) med keepLocalTime=false
                planslutt: moment(this.modell.periode.slutt).utc(false).format('L'),
                gruppetittel: this.modell.gruppe.tittel,
                organisasjonsnavn: this.modell.gruppe.organisasjonsnavn
            }),
            ///ok-knapp i dialog ved opprettelse av møter i plan-wizard
            this.gettextCatalog.getString('Opprett'),
            ///avbryt-knapp i dialog ved opprettelse av møter i plan-wizard
            this.gettextCatalog.getString('Avbryt'))
            .then(() => this.utførOpprettMøter());
    }

    //hjelpemetoder

    private initVelgPeriodeTrinn(){
        this.lastPlaner()
            .then(planer => {
                this.modell.tilgjengeligeAaarstall = _.range(2000, 2100).map(år => ({aarstall: år, eksisterendePlan: planer.find(p => p.periode.start.getUTCFullYear() === år)}));
                const inneværendeÅr = new Date().getUTCFullYear();
                //sett valgt år til første år uten eksisterende plan
                const år = this.modell.tilgjengeligeAaarstall.filter(periode => periode.aarstall >= inneværendeÅr && _.isNil(periode.eksisterendePlan))[0];// _.find(, periode => periode.aarstall >= inneværendeÅr && _.isNil(periode.eksisterendePlan))
                this.modell.setÅr(år);

                //koden under er knyttet til gui der man kunne velge fra-til dato - ikke slettet da det er aktuelt å gjeninnføre denne muligheten
                /*
                const overlappende = this.beregnOverlappendePlaner();
                if (overlappende.length == 0) return;

                //det finnes allerede en plan med periode som overlapper standard periode (som er innevørende år) - lag ny periode etter siste eksisterende
                const senestePlan: l.PlanModell = _.last(this.eksisterendePlaner);
                const sluttSenestePlan = tilLokalDatoIgnorerTs(senestePlan.periode.slutt); //settPeriode forventer datoer i lokal ts
                this.modell.settPeriode(moment(sluttSenestePlan).add(1, 'second').toDate(), moment(sluttSenestePlan).add(1, 'year').toDate());*/
            });
    }

    private initVelgKopierPlanForKopieringTrinn(){
        return this.lastPlaner();
    }

    private lastPlaner(): IPromise<l.PlanModell[]> {
        if (this.modell.eksisterendePlaner != null)
            return this.$q.resolve(this.modell.eksisterendePlaner);

        return this.planRessurs.hentAlle(this.modell.gruppe.id).$promise
            .then(planer => {
                this.modell.eksisterendePlaner = planer.reverse();
                return this.modell.eksisterendePlaner;
            });
    }

    private beregnOverlappendePlaner(){
        if (_.isNil(this.modell.spesifikasjon.periode.start) || _.isNil(this.modell.spesifikasjon.periode.slutt)) return [];
        const nyPeriode = moment(this.modell.spesifikasjon.periode.start).twix(this.modell.spesifikasjon.periode.slutt);
        return this.eksisterendePlaner.filter(plan => moment(plan.periode.start).twix(plan.periode.slutt).overlaps(nyPeriode));
    }

    private gåTilForrigeTrinn(){
        this.modell.trinn--;
        if (this.modell.trinn === OpprettPlanTrinn.velgperiode) this.initVelgPeriodeTrinn();
        if (this.modell.trinn === OpprettPlanTrinn.velgKopierPlanForKopiering) {
            this.initVelgKopierPlanForKopieringTrinn().then(eksisterendePlaner => {
                //hopp over "velg plan å kopiere fra" trinnet dersom ingen planer med møter eksisterer
                if (_.isEmpty(eksisterendePlaner.filter(plan => !_.isEmpty(plan.moeter))))
                    this.modell.trinn--;
            });
        }
    }

    private utførOpprettMøter(){
        this.planRessurs.opprettPlanMedMøter(this.modell.gruppe.id, this.modell.somSpesifikasjon()).$promise
            .then(() => {
                const filtervalg = new DashboardFiltervalg()
                filtervalg.gruppeIder = [this.modell.gruppe.id];
                filtervalg.periode = this.modell.spesifikasjon.periode;
                filtervalg.gruppering = l.SaksgrupperingPlan.tema;

                this.webNavigasjon.gåTilPlanMedFiltervalg(filtervalg);
            });
    }
}

class OpprettPlanModell {
    constructor(grupper: l.EnkelGruppeModell[]) {
        this.tilgjengeligeGrupper = grupper;
        this.reset();
    }

    alleTrinn = OpprettPlanTrinn;
    trinn: OpprettPlanTrinn = OpprettPlanTrinn.velggruppe;

    tilgjengeligeGrupper: l.EnkelGruppeModell[];
    gruppe: l.EnkelGruppeModell = null;
    /** planens periode - i lokal ts, i motsetning til spesifikasjon.periode som er i utc */
    periode: l.PlanPeriode = {start:null, slutt:null};

    tilgjengeligeAaarstall: PeriodeÅr[];
    /** foreløpig lar vi bruker kun angi årstall - ikke fra-til dato - dette for å unngå perioder som ikke dekker hele år (jan-des) */
    aarstall: PeriodeÅr;

    eksisterendePlaner:l.PlanModell[] = null;
    kopierPlan:l.PlanModell;

    spesifikasjon: l.PlanMedMøterSpesifikasjon;

    reset(){
        this.spesifikasjon = {nyeMoeter: [], kopierteMoeter: [], periode: {start:null, slutt:null}};
        this.eksisterendePlaner = null;
        this.kopierPlan = null;
        this.setÅr({aarstall: moment().year(), eksisterendePlan: null});
    }

    setÅr(år: PeriodeÅr) {
        this.aarstall = år;

        const dato = moment().utc(false).year(år.aarstall); //dato er opprinnelig i UTC - kaller derfor .utc(..) med keepLocalTime=false
        this.settPeriode(dato.startOf('year').toDate(), dato.endOf('year').toDate());
    }

    /** setter start og slutt dato for planperiod i plan som skal opprettes, forventer at angitte datoer er korrekte i UTC-tid og inge videre manipulasjon er nødv før sending til server */
    settPeriode(start:Date, slutt:Date){
        this.periode = {start:start, slutt:slutt};
        if (!_.isNil(start)) this.spesifikasjon.periode.start = (this.periode.start);
        if (!_.isNil(slutt)) this.spesifikasjon.periode.slutt = (this.periode.slutt);
    }

    settPeriodeOgÅr(start:Date, slutt:Date){
        this.settPeriode(start, slutt);
        this.aarstall.aarstall = this.spesifikasjon.periode.start.getFullYear();
    }

    antallMoeter(){
        return this.spesifikasjon.nyeMoeter.length + this.spesifikasjon.kopierteMoeter.length;
    }

    somSpesifikasjon(): l.PlanMedMøterSpesifikasjon {
        return this.spesifikasjon;
    }
}

interface PeriodeÅr {
    aarstall:number;
    eksisterendePlan: l.PlanModell | null;
}

enum OpprettPlanTrinn {
    velggruppe, velgperiode, vismoetemal, velgKopierPlanForKopiering, visplan
}
